<template>
  <div class="sd-card">
    <div class="ms-3 mb-4">
        <div class="mb-0 text-sm text-uppercase text-primary font-weight-bold">
            {{ dateRange }}
        </div>
        <h5 id="header" class="mb-0 font-weight-bolder mt-1">
                {{ title }}
        </h5>
    </div>
    <div v-if="sleepFeatures.length > 0" class="chart-wrap">
      <canvas ref="canvasRef"></canvas>
    </div>
    <div v-else>No data found for the selected date range.</div>

    <!-- Summary strip -->
    <div v-if="sleepFeatures.length > 0" class="summary">
      <div class="pill-accent"></div>

      <div class="summary-item">
        <div class="k">Sleep Debt</div>
        <div class="v">{{ chartSummary.sleepDebtRange }}</div>
      </div>

      <div class="summary-item">
        <div class="k">Days in Debt</div>
        <div class="v">{{ chartSummary.daysInDebt }}</div>
      </div>

      <div class="summary-item">
        <div class="k">Consecutive Days in Debt</div>
        <div class="v">{{ chartSummary.consecutiveDaysInDebt }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, LineController, LineElement, PointElement, CategoryScale, LinearScale,  Tooltip, Legend, Filler, } from "chart.js";
Chart.register( LineController, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler );

// ---- helpers ----
function hmToMinutes(hm) {
  // "2:15" -> 135, "0:20" -> 20
  const [h, m] = String(hm).split(":").map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}
function minutesToHMLabel(mins) {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, "0");
  return `${h}:${mm}`;
}

export default {
  name: "SleepDebtLineChart",
  props: {
    title: { 
        type: String, 
        default: "Sleep Debt" 
    },
    dateRange: { 
        type: String, 
        default: "" 
    },
    sleepFeatures: {
      type: Array,
      default: () => [],
      required: true,
    },
  },
  data() {
    return { 
        chart: null,
        summary: {
            sleepDebtRange: "",
            daysInDebt: "",
            consecutiveDaysInDebt: "",
      },
      lineColor: "#f6b100", // orange/yellow
      tickColor:  "#f7eed5",
    };
  },

  mounted() {
    this.renderChart();
    window.addEventListener("resize", this.onResize, { passive: true });
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
    this.destroyChart();
  },

  computed: {
    chartSummary() {
      if (this.sleepFeatures && this.sleepFeatures.length > 0) {
        return this.generateSummaryFromFeatures(this.sleepFeatures);
      }
      return this.summary;
    },
    chartPoints() {
      if (this.sleepFeatures && this.sleepFeatures.length > 0) {
        return this.convertSleepFeaturesToPoints(this.sleepFeatures);
      }
      return [];
    },
  },
  watch: {
    chartPoints: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.renderChart();
        });
      },
    },
  },
  methods: {
    generateSummaryFromFeatures(features) {
      if (!features || features.length === 0) {
        return {
          sleepDebtRange: "N/A",
          daysInDebt: "N/A",
          consecutiveDaysInDebt: "N/A",
        };
      }

      const debts = features.map((f) => f.sleep_debt || 0);
      const minDebt = Math.min(...debts);
      const maxDebt = Math.max(...debts);

      const daysInDebtCount = debts.filter((d) => d > 0).length;
      const daysInDebtPct = features.length > 0 ? Math.round(
        (daysInDebtCount / features.length) * 100
      ) : 0;

      let maxConsecutive = 0;
      let currentConsecutive = 0;
      for (const debt of debts) {
        if (debt > 0) {
          currentConsecutive++;
        } else {
          maxConsecutive = Math.max(maxConsecutive, currentConsecutive);
          currentConsecutive = 0;
        }
      }
      maxConsecutive = Math.max(maxConsecutive, currentConsecutive);

      return {
        sleepDebtRange: `${minutesToHMLabel(minDebt)} - ${minutesToHMLabel(
          maxDebt
        )}`,
        daysInDebt: `${daysInDebtCount} of ${features.length}d (${daysInDebtPct}%)`,
        consecutiveDaysInDebt: String(maxConsecutive),
      };
    },
    convertSleepFeaturesToPoints(features) {
      if (!features || features.length === 0) {
        return [];
      }
      return features.map((f) => {
        const parts = f.bucket.split("-").map((p) => parseInt(p, 10));
        const bucketDate = new Date(parts[0], parts[1] - 1, parts[2]);

        const sleepDate = bucketDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        const sleepDay = bucketDate
          .toLocaleDateString("en-US", { weekday: "short" })
          .toUpperCase();

        const debt = f.sleep_debt ? minutesToHMLabel(f.sleep_debt) : "0:0";

        return {
          sleepDate,
          sleepDay,
          debt,
        };
      });
    },

    onResize() {
      if (this.chart) this.chart.resize();
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },

    makePointValueLabelsPlugin() {
      const vm = this;
      return {
        id: "pointValueLabels",
        afterDatasetsDraw(chart) {
          const dsIndex = 0;
          const meta = chart.getDatasetMeta(dsIndex);
          if (!meta?.data?.length) return;

          const { ctx } = chart;
          ctx.save();
          ctx.font = "12px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillStyle = vm.lineColor;

          meta.data.forEach((pt, i) => {
            const v = chart.data.datasets[dsIndex].data[i];
            // hide labels for exact 0 to match clean look (optional)
            if (Number(v) === 0) return;
            ctx.fillText(minutesToHMLabel(v), pt.x, pt.y - 10);
          });

          ctx.restore();
        },
      };
    },

    renderChart() {
      this.destroyChart();

      if (!this.chartPoints || this.chartPoints.length === 0) {
        return;
      }

      const ctx = this.$refs.canvasRef?.getContext("2d");
      if (!ctx) return;

      const labels = this.chartPoints.map((p) => [p.sleepDate, p.sleepDay]); // 2-line ticks
      const values = this.chartPoints.map((p) => hmToMinutes(p.debt));
      // Gradient fill (right-side glow like screenshot)
      const grad = ctx.createLinearGradient(0, 0, ctx.canvas.width, 0);
      grad.addColorStop(0, "rgba(246,177,0,0.00)");
      grad.addColorStop(0.7, "rgba(246,177,0,0.04)");
      grad.addColorStop(1, "rgba(246,177,0,0.06)");

      this.chart = new Chart(ctx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Sleep Debt",
              data: values,
              borderColor: this.lineColor,
              backgroundColor: grad,
              fill: true,
              tension: 0.28,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 4,
              pointBackgroundColor: this.lineColor,
              pointBorderColor: this.lineColor,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 14, right: 18, bottom: 0, left: 10 } },
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                color: this.tickColor,
                boxWidth: 15,
                boxHeight: 15,
              },
            },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const lbl = items?.[0]?.label;
                  if (Array.isArray(lbl)) return `${lbl[0]} ${lbl[1]}`;
                  return String(lbl || "");
                },
                label: (ctx) => `Sleep Debt: ${minutesToHMLabel(ctx.parsed.y)}`,
              },
            },
          },
          scales: {
            x: {
                grid: {
                    display: true,                 // enable vertical guides
                    drawBorder: false,
                    color: "rgba(255,255,255,0.08)",   // subtle lines like screenshot
                    lineWidth: 1
                },
                ticks: {
                    color: this.tickColor,
                    font: { size: 11 },
                    maxRotation: 0,
                    minRotation: 0
                }
            },
            y: {
              beginAtZero: true,
              suggestedMax: 140, // matches screenshot scale
              ticks: {
                color: this.tickColor,
                stepSize: 20,
              },
              title: {
                display: true,
                text: "MINUTES",
                color: this.tickColor,
                font: { size: 12, weight: "600" },
              },
              grid: { display: false, drawBorder: false }, // no rectangles
            },
          },
        },
        plugins: [this.makePointValueLabelsPlugin()],
      });
    },
  },
};
</script>

<style scoped>
.sd-card {
  width: 100%;
  background: #233334;
  border-radius: 12px;
  padding: 14px 14px 10px;
  box-sizing: border-box;
}

.chart-wrap {
  height: 260px; /* close to screenshot */
  width: 100%;
}

.summary {
  margin: 14px auto 0;
  max-width: 720px;
  width: 100%;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 10px repeat(3, 1fr);
  gap: 14px;
  align-items: center;
  box-sizing: border-box;
}

.pill-accent {
  width: 6px;
  height: 100%;
  border-radius: 999px;
  background: #f5dc9e;
  opacity: 0.95;
  justify-self: start;
}

.summary-item .k {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 4px;
  white-space: nowrap;
}

.summary-item .v {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 600;
  white-space: nowrap;
}

@media (max-width: 820px) {
  .summary {
    grid-template-columns: 10px 1fr;
    row-gap: 10px;
  }
}
</style>
